/**
 * Phase 4b PR 3 — generic Error Boundary.
 *
 * React class-component error boundary with a minimal fallback UI and
 * a "Reload page" reset action. Wraps the whole app at main.tsx so an
 * uncaught render error doesn't blank the screen.
 *
 * Intentionally minimal:
 *   - No external logging (no Sentry yet — Phase 4a/b doesn't introduce it)
 *   - No fancy retry — page reload is the simplest safe reset
 *   - Falls back to ink-on-cream styling consistent with Ink & Marine
 *
 * The fallback uses inline styles so it survives even if CSS Modules
 * or token CSS failed to load (which would itself trigger this boundary).
 * The four font sizes read a `--ramp-bridge-N` token WITH the old px as its
 * fallback: with tokens.css loaded they follow the type ramp, and without it
 * they still compute to the sizes they had before the ramp.
 *
 * Phase 5+ may swap in a more elaborate fallback once we have an error-
 * reporting service wired up. The boundary's contract — render
 * `children` until something throws, then render fallback — stays.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Optional override for the fallback UI. Default = inline minimal panel. */
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] Uncaught render error:', error, info?.componentStack);
  }

  reset = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    if (this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.reset);
      }
      return <DefaultFallback error={this.state.error} onReload={() => window.location.reload()} />;
    }
    return this.props.children;
  }
}

interface DefaultFallbackProps {
  error: Error;
  onReload: () => void;
}

function DefaultFallback({ error, onReload }: DefaultFallbackProps): JSX.Element {
  // Inline styles intentionally — survive CSS load failure (which
  // could itself be the error that triggered the boundary).
  const containerStyle: React.CSSProperties = {
    fontFamily: '"DM Sans", system-ui, sans-serif',
    backgroundColor: '#F7F7F5', // --cream
    color: '#0F1412', // --ink-900
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
  };
  const cardStyle: React.CSSProperties = {
    maxWidth: '480px',
    backgroundColor: '#FFFFFF', // --paper
    border: '1px solid #E5E5E0',
    borderRadius: '12px',
    padding: '32px',
    boxShadow: '0 1px 2px rgba(15, 20, 18, 0.04)',
  };
  const headingStyle: React.CSSProperties = {
    fontFamily: '"Instrument Serif", Georgia, serif',
    fontSize: 'var(--ramp-bridge-32, 32px)',
    fontWeight: 400,
    margin: '0 0 12px 0',
    color: '#0F1412',
  };
  const bodyStyle: React.CSSProperties = {
    fontSize: 'var(--ramp-bridge-16, 16px)',
    lineHeight: 1.5,
    margin: '0 0 24px 0',
    color: '#0F1412',
  };
  const buttonStyle: React.CSSProperties = {
    backgroundColor: '#1B2A4E', // --signal
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '8px',
    padding: '10px 20px',
    fontSize: 'var(--ramp-bridge-15, 15px)',
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: 'inherit',
  };
  const detailsStyle: React.CSSProperties = {
    marginTop: '24px',
    fontSize: 'var(--ramp-bridge-13, 13px)',
    color: '#6B6B6B',
    fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  };

  return (
    <div style={containerStyle} role="alert">
      <div style={cardStyle}>
        <h1 style={headingStyle}>Something went wrong</h1>
        <p style={bodyStyle}>
          The page hit an unexpected error. Reloading usually fixes this. If it keeps
          happening, tell us what you were doing.
        </p>
        <button type="button" style={buttonStyle} onClick={onReload}>
          Reload page
        </button>
        {error.message ? (
          <details style={detailsStyle}>
            <summary>Error details</summary>
            {error.message}
          </details>
        ) : null}
      </div>
    </div>
  );
}
